'use client';

import * as React from 'react';
import {
  useController,
  UseControllerProps,
  FieldValues,
  FieldPath,
  Control,
  PathValue,
} from 'react-hook-form';

import { MultiSelect, MultiSelectProps } from '../multi-select';

export interface FormMultiSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<MultiSelectProps, 'name' | 'value' | 'onChange' | 'ref'> {
  /**
   * Field name for React Hook Form
   */
  name: TName;

  /**
   * Control from React Hook Form
   */
  control?: Control<TFieldValues>;

  /**
   * Validation rules for React Hook Form
   */
  rules?: UseControllerProps<TFieldValues, TName>['rules'];

  /**
   * Whether to show the error message
   * @default true
   */
  showError?: boolean;
}

/**
 * MultiSelect component integrated with React Hook Form
 * Automatically handles form registration and validation
 *
 * @example
 * ```tsx
 * <FormMultiSelect
 *   name="tags"
 *   control={control}
 *   label="Tags"
 *   options={[
 *     { value: 'urgent', label: 'Urgent' },
 *     { value: 'bug', label: 'Bug' },
 *     { value: 'feature', label: 'Feature' }
 *   ]}
 *   rules={{
 *     required: 'Please select at least one tag',
 *     validate: (value) => value.length > 0 || 'At least one tag is required'
 *   }}
 *   maxItems={5}
 * />
 * ```
 */
export function FormMultiSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, control, rules, showError = true, ...props }: FormMultiSelectProps<TFieldValues, TName>) {
  const {
    field: { onChange, onBlur, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
    defaultValue: [] as PathValue<TFieldValues, TName>, // Default to empty array for multi-select
  });

  // Ensure value is always an array
  const arrayValue = React.useMemo(() => {
    if (Array.isArray(value)) return value;
    if (value === null || value === undefined) return [];
    return [value];
  }, [value]);

  return (
    <MultiSelect
      {...props}
      ref={ref}
      value={arrayValue}
      onChange={(values) => {
        onChange(values);
        onBlur();
      }}
      error={showError ? error?.message : undefined}
    />
  );
}

FormMultiSelect.displayName = 'FormMultiSelect';
